import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import { genres, decades } from '@/mocks/movies';
import { filterMovies, getAllYears, paginate, type SortKey } from '@/utils/movie';

const PAGE_SIZE = 8;

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'latest', label: '按上映时间' },
  { key: 'rating', label: '按评分' },
  { key: 'popularity', label: '按热度' },
];

const ratingOptions = [
  { value: '0', label: '不限评分' },
  { value: '8.5', label: '8.5 分以上' },
  { value: '8.0', label: '8.0 分以上' },
  { value: '7.5', label: '7.5 分以上' },
];

export default function Category() {
  const [params, setParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const genre = params.get('genre') || '';
  const decade = params.get('decade') || '';
  const year = params.get('year') || '';
  const minRating = params.get('rating') || '0';
  const sort = (params.get('sort') as SortKey) || 'latest';
  const page = Number(params.get('page') || '1');

  const years = getAllYears();

  const update = (next: Record<string, string | null>, resetPage = true) => {
    const merged: Record<string, string> = {};
    params.forEach((value, key) => {
      merged[key] = value;
    });
    Object.entries(next).forEach(([k, v]) => {
      if (v === null || v === '') delete merged[k];
      else merged[k] = v;
    });
    if (resetPage) delete merged.page;
    setParams(merged);
    setFiltersOpen(false);
  };

  const result = useMemo(
    () =>
      filterMovies({
        genre: genre || undefined,
        decade: decade || undefined,
        year: year ? Number(year) : undefined,
        minRating: Number(minRating) || undefined,
        sort,
      }),
    [genre, decade, year, minRating, sort],
  );

  const { items, totalPages, total } = paginate(result, page, PAGE_SIZE);

  const currentGenre = genre || '';
  const pageTitle = currentGenre ? `${currentGenre}电影` : '全部电影';
  const breadcrumbCurrent = currentGenre || (decade ? decade.split('-').join(' - ') + ' 年代' : '全部分类');

  useEffect(() => {
    document.title = `李采潭电影 - 分类：${pageTitle}`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', `浏览李采潭电影分类「${pageTitle}」，可按类型、年代、评分筛选并排序，共收录 ${total} 部影视作品。`);
  }, [pageTitle, total]);

  const hasFilter = Boolean(genre || decade || year || (minRating && minRating !== '0'));

  const resetAll = () => setParams({});

  const FilterPanel = (
    <div className="space-y-6">
      <div>
        <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3">类型</h3>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => update({ genre: null })}
            className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${
              !genre ? 'bg-primary-700 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            全部
          </button>
          {genres.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => update({ genre: g })}
              className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${
                genre === g ? 'bg-primary-700 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3">年代</h3>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => update({ decade: null })}
            className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${
              !decade ? 'bg-secondary-600 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            全部
          </button>
          {decades.map((d) => {
            const key = `${d.start}-${d.end}`;
            return (
              <button
                key={key}
                type="button"
                onClick={() => update({ decade: key })}
                className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${
                  decade === key ? 'bg-secondary-600 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {d.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3">上映年份</h3>
        <select
          value={year}
          onChange={(e) => update({ year: e.target.value })}
          className="w-full h-10 px-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer"
        >
          <option value="">全部年份</option>
          {years.map((y) => (
            <option key={y} value={y}>{y} 年</option>
          ))}
        </select>
      </div>

      <div>
        <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3">评分区间</h3>
        <select
          value={minRating}
          onChange={(e) => update({ rating: e.target.value === '0' ? null : e.target.value })}
          className="w-full h-10 px-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer"
        >
          {ratingOptions.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      {hasFilter && (
        <button
          type="button"
          onClick={resetAll}
          className="w-full h-10 rounded-md border border-background-300 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer flex items-center justify-center gap-2"
        >
          <i className="ri-refresh-line"></i>重置全部筛选
        </button>
      )}
    </div>
  );

  return (
    <div className="w-full">
      {/* header */}
      <div className="w-full bg-primary-900 text-background-50">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <nav className="flex items-center flex-wrap gap-2 text-xs text-primary-200/80 mb-4">
            <Link to="/" className="hover:text-accent-300 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>电影分类</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-accent-300">{breadcrumbCurrent}</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-4xl font-bold">李采潭电影 · {pageTitle}</h1>
          <p className="text-sm text-primary-200/85 mt-3 max-w-2xl">
            共收录 <span className="text-accent-300 font-semibold">{total}</span> 部符合当前条件的作品，可按类型、年代、评分筛选，并支持按时间、评分与热度排序。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* sidebar */}
          <aside className="lg:w-[280px] shrink-0">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="lg:hidden w-full h-11 mb-4 rounded-md bg-background-100 border border-background-300 text-sm font-medium text-foreground-800 flex items-center justify-between px-4 cursor-pointer"
            >
              <span className="flex items-center gap-2"><i className="ri-filter-3-line"></i>筛选条件</span>
              <i className={filtersOpen ? 'ri-arrow-up-s-line text-xl' : 'ri-arrow-down-s-line text-xl'}></i>
            </button>
            <div className={`${filtersOpen ? 'block' : 'hidden'} lg:block lg:sticky lg:top-20 rounded-lg bg-background-50 border border-background-200 p-5`}>
              {FilterPanel}
            </div>
          </aside>

          {/* results */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <p className="text-sm text-foreground-600">
                找到 <span className="font-semibold text-foreground-950">{total}</span> 部作品
                {hasFilter && <button type="button" onClick={resetAll} className="ml-3 text-primary-700 hover:underline cursor-pointer text-xs">清除筛选</button>}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序</span>
                <select
                  value={sort}
                  onChange={(e) => update({ sort: e.target.value }, false)}
                  className="h-10 px-3 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer"
                >
                  {sortOptions.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {items.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {items.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg bg-background-100 border border-background-200 py-20 px-6 text-center">
                <span className="inline-flex w-16 h-16 rounded-full bg-background-200 items-center justify-center mb-5">
                  <i className="ri-movie-2-line text-3xl text-foreground-400"></i>
                </span>
                <h3 className="font-heading text-lg font-semibold text-foreground-950">没有找到符合条件的电影</h3>
                <p className="text-sm text-foreground-500 mt-2">试试放宽筛选条件，或重置后重新浏览全部作品。</p>
                <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                  <button
                    type="button"
                    onClick={resetAll}
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-700 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-800 transition-colors cursor-pointer"
                  >
                    <i className="ri-refresh-line"></i>重置筛选
                  </button>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    返回首页
                  </Link>
                </div>
              </div>
            )}

            {totalPages > 1 && items.length > 0 && (
              <div className="flex items-center justify-center flex-wrap gap-2 mt-10">
                <button
                  type="button"
                  onClick={() => update({ page: String(page - 1) }, false)}
                  disabled={page <= 1}
                  className="h-10 px-3 rounded-md border border-background-300 text-sm text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => update({ page: String(p) }, false)}
                    className={`w-10 h-10 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                      p === page ? 'bg-primary-700 text-background-50' : 'border border-background-300 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => update({ page: String(page + 1) }, false)}
                  disabled={page >= totalPages}
                  className="h-10 px-3 rounded-md border border-background-300 text-sm text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}